/**
 * 风格缩略条(2026-08-06 用户审阅:「风格是否可以扎眼一些,这样可以吸引用户」)。
 *
 * ═══ 换掉了什么 ═══════════════════════════════════════════════════════════════
 * 重排第一版把风格压成一颗 44×30 缩略 + 名称的小按钮(与比例/时长并排)。
 * 那是"省地方"的做法 —— 但风格是这一页**唯一能一眼看出成片长什么样**的东西,
 * 把它做成三个同权重控件里最小的一个,等于把最有吸引力的能力藏起来。
 * 现在给它独占一行:直接铺缩略图,用户看图选,不看文字。
 *
 * ═══ 铺哪几张 ═══════════════════════════════════════════════════════════════
 * 库里 31 种风格,全铺会把备料台撑爆(④ 编排按钮会掉出首屏 —— 那是本次重排的核心收益,
 * 不能为了好看赔掉)。所以取**每个媒介的第一张**当代表(实拍/三维/动图/二维/绘画/定格),
 * 一条就把风格跨度亮完;想细挑点「更多」进画廊(31 种 + 搜索 + 自定义画风)。
 *
 * ★当前选中的若不在条上(用户从画廊挑的 / 自定义画风),**额外补一张**放在最前 ——
 *   否则会出现"我明明选了却哪张都没亮"的状态,比不显示更让人怀疑没生效。
 */
import { Plus } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { VideoStyleThumb } from "./VideoStyleThumb";
import {
  FREE_VIDEO_STYLE,
  isCustomVideoTone,
  PRESET_VIDEO_STYLES,
  stripVideoStyles,
  videoStyleByTone,
} from "./videoStyleOptions";

interface Props {
  /** 当前 tone(null = 自由发挥;非预设串 = 自定义画风) */
  briefStyle: string | null;
  onChange: (tone: string | null, key?: string) => void;
  /** 打开画廊(31 种 + 搜索 + 自定义)。 */
  onMore: () => void;
  disabled?: boolean;
  /**
   * 租户行业码(1004 第三轮):消费向行业(珠宝等)条上只铺适配的风格(与军师侧同口径);
   * 其余行业 / 取不到 → 每个媒介一张(旧口径)。
   */
  industryCode?: string;
}

export function VideoStyleStrip({ briefStyle, onChange, onMore, disabled, industryCode }: Props) {
  const REPRESENTATIVES = stripVideoStyles(industryCode);
  const custom = isCustomVideoTone(briefStyle);
  const picked = videoStyleByTone(briefStyle);
  // 选中的不在代表里 → 补一张(自定义画风没有 option,单独渲一个文字 tile)
  const extra = picked && !REPRESENTATIVES.some((r) => r.tone === picked.tone) ? picked : null;

  return (
    <div
      data-video-style-strip
      // ★←/→ 在条内走(2026-08-07 走查:此前方向键完全没反应)。条在视觉上是一行并排的
      //   同类项,方向键是这类控件的常规预期;Tab 会把焦点送出条外,不顶用。
      //   ★只移焦点、不改选中:扫过去就换风格会让用户莫名其妙丢掉自己的选择
      //   (选中仍走点击 / Enter / 空格,那是 button 的原生行为,这里不拦)。
      onKeyDown={(e) => {
        if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
        const tiles = [...e.currentTarget.querySelectorAll("button")] as HTMLButtonElement[];
        const at = tiles.indexOf(document.activeElement as HTMLButtonElement);
        if (at < 0) return;
        // 两端夹住不绕回:绕回时用户按到头会以为"跳走了",clamp 更好读
        const next = Math.min(
          tiles.length - 1,
          Math.max(0, at + (e.key === "ArrowRight" ? 1 : -1))
        );
        if (next === at) return;
        e.preventDefault();
        tiles[next].focus();
      }}
      className={cn("flex flex-wrap gap-2", disabled && "pointer-events-none opacity-60")}
    >
      {/* 自由发挥常驻首位:它是默认档,也是"我不想管"的正当出口 */}
      <VideoStyleThumb
        option={FREE_VIDEO_STYLE}
        selected={briefStyle === null}
        onSelect={() => onChange(null)}
        size="sm"
        className="w-[96px] shrink-0"
      />

      {custom && (
        // 自定义画风没有样例图,给一张写着用户原话的 tile —— 至少让他看得见自己选了什么
        <button
          type="button"
          onClick={onMore}
          aria-pressed
          title={briefStyle ?? ""}
          className="flex w-[96px] shrink-0 flex-col overflow-hidden rounded-lg border border-primary text-left ring-2 ring-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          <span
            className="flex w-full items-center justify-center px-1.5 text-center text-2xs leading-tight text-white/95"
            style={{ aspectRatio: "16 / 9", background: "linear-gradient(135deg,#7c3aed,#22d3ee)" }}
          >
            自定义画风
          </span>
          <span className="truncate px-1.5 py-1 text-center text-xs font-medium text-primary">
            {briefStyle}
          </span>
        </button>
      )}

      {extra && (
        <VideoStyleThumb
          option={extra}
          selected
          onSelect={() => onChange(extra.tone, extra.key)}
          size="sm"
          className="w-[96px] shrink-0"
        />
      )}

      {REPRESENTATIVES.map((o) => (
        <VideoStyleThumb
          key={o.key ?? o.label}
          option={o}
          selected={briefStyle === o.tone}
          onSelect={() => onChange(o.tone, o.key)}
          size="sm"
          className="w-[96px] shrink-0"
        />
      ))}

      {/* 更多:31 种 + 搜索 + 自定义画风都在画廊里 */}
      <button
        type="button"
        onClick={onMore}
        className="flex w-[96px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border/70 text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <Plus className="h-4 w-4" />
        <span className="text-xs font-medium">更多风格</span>
        <span className="text-2xs">共 {PRESET_VIDEO_STYLES.length} 种</span>
      </button>
    </div>
  );
}
